import React, { useState } from 'react';
import { SVGIcon } from '@uikit/old/Icon/Icon';

import Input from '@uikit/Form/Input/Input';
import { normalizeWebUrl } from '@utils/url.utils';
import { Button } from '@uikit/Form/Button/Button';

import './SubmitNewLiveUrl.scss';

const NEW_LIVE_URL_PLACEHOLDER = 'https://example.com/article-name';
const MAX_URL_LENGTH = 200;
const INVALID_URL_ERROR = 'Enter a valid URL';
const TOO_LONG_URL_ERROR = `URL must be ${MAX_URL_LENGTH} characters or fewer`;

type SubmitNewLiveUrlPropsType = {
  /**
   * Resolves when the URL is accepted; the panel then collapses and clears.
   * On rejection the panel stays open with the draft intact — the parent
   * is responsible for the error notification.
   */
  onSubmit: (newLiveUrl: string) => Promise<unknown>;
};

function SubmitNewLiveUrl({ onSubmit }: SubmitNewLiveUrlPropsType): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isSubmitDisabled = !url.trim().length || isSubmitting;

  const handleCollapse = () => {
    if (isSubmitting) {
      return;
    }

    setIsExpanded(false);
    setUrl('');
    setError('');
  };

  const handleSubmit = () => {
    if (isSubmitDisabled) {
      return;
    }

    const trimmedUrl = url.trim();

    setError('');

    const normalizedUrl = normalizeWebUrl(trimmedUrl);
    if (!normalizedUrl) {
      setError(INVALID_URL_ERROR);
      return;
    }

    if (normalizedUrl.length > MAX_URL_LENGTH) {
      setError(TOO_LONG_URL_ERROR);
      return;
    }

    setIsSubmitting(true);

    onSubmit(normalizedUrl)
      .then(() => {
        setIsExpanded(false);
        setUrl('');
      })
      .catch(() => {
        // The parent already notified the user; keep the draft for a retry.
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) {
      return;
    }

    event.preventDefault();
    handleSubmit();
  };

  if (!isExpanded) {
    return (
      <button
        type="button"
        className="submit-new-live-url__toggle"
        onClick={() => setIsExpanded(true)}
        data-testid="change-request-live-url-toggle"
      >
        Submit new live URL
        <SVGIcon icon="arrowUpRight" size={18} />
      </button>
    );
  }

  return (
    <div className="submit-new-live-url__panel">
      <button type="button" className="submit-new-live-url__header" onClick={handleCollapse}>
        Submit new live URL
        <SVGIcon icon="chevronUp" size={18} />
      </button>
      <div className="submit-new-live-url__description">
        This replaces the current live URL and goes to the client for approval.
      </div>
      <div className="submit-new-live-url__field" data-testid="change-request-live-url-field">
        <Input
          placeholder={NEW_LIVE_URL_PLACEHOLDER}
          data-testid="change-request-live-url-input"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          onKeyDown={handleKeyDown}
          error={error}
          isFullWidth
        />
      </div>
      <div className="submit-new-live-url__footer">
        <Button
          size="l"
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          data-testid="change-request-live-url-submit"
        >
          Submit for approval
        </Button>
        <Button
          type="ghost"
          size="l"
          disabled={isSubmitting}
          onClick={handleCollapse}
          data-testid="change-request-live-url-cancel"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default SubmitNewLiveUrl;
